import { useCallback, useEffect, useRef, useState } from "react"
import { Box, Button, Stack, Typography } from "@mui/material"
import { getAudioReport, cleanupStaleAudios } from "utils/audioManager"

const POLL_MS = 1000
const MAX_HISTORY = 120

const isEnabled = () => {
  if (process.env.NODE_ENV === "production") return false
  if (typeof window === "undefined") return false
  return new URLSearchParams(window.location.search).has("audioStress")
}

const bytesToBase64 = (bytes) => {
  let binary = ""
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i])
  }
  return window.btoa(binary)
}

const buildWav = ({ samples, sampleRate = 8000, frequency = 0 }) => {
  const dataSize = samples * 2
  const buffer = new ArrayBuffer(44 + dataSize)
  const view = new DataView(buffer)
  const writeStr = (offset, str) => {
    for (let i = 0; i < str.length; i += 1) {
      view.setUint8(offset + i, str.charCodeAt(i))
    }
  }

  writeStr(0, "RIFF")
  view.setUint32(4, 36 + dataSize, true)
  writeStr(8, "WAVE")
  writeStr(12, "fmt ")
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true)
  view.setUint16(22, 1, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * 2, true)
  view.setUint16(32, 2, true)
  view.setUint16(34, 16, true)
  writeStr(36, "data")
  view.setUint32(40, dataSize, true)

  for (let i = 0; i < samples; i += 1) {
    const value = frequency
      ? Math.sin((2 * Math.PI * frequency * i) / sampleRate) * 12000
      : 0
    view.setInt16(44 + i * 2, value, true)
  }

  return `data:audio/wav;base64,${bytesToBase64(new Uint8Array(buffer))}`
}

const buildSilentClip = (seconds = 4) => buildWav({ samples: 8000 * seconds })
const buildTone = (frequency = 660, seconds = 0.25) =>
  buildWav({ samples: Math.floor(8000 * seconds), frequency })

const toneColor = (alive, limit) => {
  const ratio = alive / limit
  if (ratio >= 1) return "#f44336"
  if (ratio >= 0.75) return "#ff9800"
  return "#4caf50"
}

export const AudioStressPanel = () => {
  const [enabled] = useState(isEnabled)
  const [report, setReport] = useState(null)
  const [history, setHistory] = useState([])
  const [status, setStatus] = useState("inactivo")
  const [busy, setBusy] = useState(false)

  const leakedRef = useRef([])
  const previousRef = useRef(null)

  const poll = useCallback(() => {
    const current = getAudioReport()
    setReport(current)
    setHistory((prev) => [...prev, current.alive].slice(-MAX_HISTORY))
    previousRef.current = current
    return current
  }, [])

  useEffect(() => {
    if (!enabled) return undefined
    poll()
    const timer = setInterval(poll, POLL_MS)
    return () => clearInterval(timer)
  }, [enabled, poll])

  const releaseLeaked = useCallback(() => {
    const count = leakedRef.current.length
    leakedRef.current.forEach((audio) => {
      try {
        audio.pause()
        audio.src = ""
        audio.removeAttribute("src")
        audio.load()
      } catch (e) {
        /* noop */
      }
    })
    leakedRef.current = []
    setStatus(`liberados ${count} elementos del simulador`)
    poll()
  }, [poll])

  // Mimics the real leak shape: a fresh element per play, never released.
  const simulateSession = useCallback(
    async (rounds = 30, perRound = 13) => {
      setBusy(true)
      setStatus(`simulando ${rounds} navegaciones x ${perRound} audios...`)
      const clip = buildSilentClip(4)
      for (let round = 0; round < rounds; round += 1) {
        for (let i = 0; i < perRound; i += 1) {
          const audio = new Audio(clip)
          leakedRef.current.push(audio)
        }
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 60))
      }
      setStatus(
        `sesión simulada: ${rounds}x${perRound} = ${leakedRef.current.length} elementos vivos`
      )
      setBusy(false)
      poll()
    },
    [poll]
  )

  // The Memorama pattern: one element per card flip, ref dropped without release.
  const simulateCardFlips = useCallback(
    async (flips = 60) => {
      setBusy(true)
      setStatus(`simulando ${flips} volteos de carta...`)
      const clip = buildSilentClip(4)
      for (let i = 0; i < flips; i += 1) {
        const audio = new Audio(clip)
        leakedRef.current.push(audio)
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 40))
      }
      setStatus(`${flips} volteos: un elemento por volteo, sin liberar`)
      setBusy(false)
      poll()
    },
    [poll]
  )

  // Fills the registry up to the limit to observe what the app does at the edge.
  const simulateCrisis = useCallback(
    async (target = 60) => {
      setBusy(true)
      setStatus(`llenando hasta ${target} elementos...`)
      const clip = buildSilentClip(4)
      let guard = 0
      while (getAudioReport().alive < target && guard < 5000) {
        const audio = new Audio(clip)
        leakedRef.current.push(audio)
        guard += 1
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 5))
      }
      setStatus(
        `crisis simulada: ${getAudioReport().alive} vivos de ${
          getAudioReport().limit
        }`
      )
      setBusy(false)
      poll()
    },
    [poll]
  )

  const testTone = useCallback(() => {
    const audio = new Audio(buildTone())
    audio.play().catch(() => {
      setStatus("el navegador bloqueó el tono (probable límite alcanzado)")
    })
    poll()
  }, [poll])

  if (!enabled) return null

  const alive = report?.alive ?? 0
  const limit = report?.limit ?? 65
  const color = toneColor(alive, limit)
  const max = Math.max(1, ...history)

  return (
    <Box
      sx={{
        position: "fixed",
        right: 16,
        bottom: 16,
        zIndex: 20000,
        width: 340,
        p: 1.5,
        borderRadius: 2,
        background: "#101418",
        color: "#e6edf3",
        fontFamily: "monospace",
        fontSize: 11,
        boxShadow: "0 8px 28px rgba(0,0,0,.45)",
        border: `1px solid ${color}`,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#7ee787" }}>
          AUDIO STRESS
        </Typography>
        <Typography sx={{ fontSize: 16, fontWeight: 700, color }}>
          {alive}/{limit}
        </Typography>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 0.5,
          my: 1,
        }}
      >
        {[
          ["tracked", report?.tracked],
          ["alive", report?.alive],
          ["released", report?.released],
          ["pruned", report?.pruned],
          ["reusable", report?.reusableAlive],
          ["pending", report?.pending],
          ["dup sources", report?.duplicatedSources],
        ].map(([label, value]) => (
          <Box key={label} sx={{ color: "#8b949e" }}>
            {label}:{" "}
            <Box component="span" sx={{ color: "#e6edf3" }}>
              {value ?? "-"}
            </Box>
          </Box>
        ))}
      </Box>

      <Stack
        direction="row"
        alignItems="flex-end"
        spacing="2px"
        sx={{ height: 40, mb: 1, borderBottom: "1px solid #30363d" }}
      >
        {history.map((value, index) => (
          <Box
            key={index}
            sx={{
              flex: 1,
              minWidth: 2,
              height: `${Math.max(4, (value / max) * 100)}%`,
              background: value / limit >= 1 ? "#f44336" : "#58a6ff",
            }}
          />
        ))}
      </Stack>

      <Box sx={{ color: "#8b949e", mb: 1, minHeight: 26 }}>{status}</Box>

      {report?.topOffenders?.length > 0 && (
        <Box sx={{ mb: 1, maxHeight: 74, overflow: "auto" }}>
          {report.topOffenders
            .filter((entry) => entry.count > 1)
            .slice(0, 4)
            .map((entry) => (
              <Box key={entry.src} sx={{ color: "#ffa657" }}>
                x{entry.count} {entry.src.slice(0, 44)}
              </Box>
            ))}
        </Box>
      )}

      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        <Button size="small" disabled={busy} onClick={() => simulateSession()}>
          sesión 30x13
        </Button>
        <Button
          size="small"
          disabled={busy}
          onClick={() => simulateCardFlips()}
        >
          volteos
        </Button>
        <Button
          size="small"
          disabled={busy}
          onClick={() => simulateCrisis()}
          sx={{ color: "#f44336" }}
        >
          crisis
        </Button>
        <Button size="small" onClick={testTone}>
          oír tono
        </Button>
        <Button size="small" onClick={releaseLeaked}>
          liberar
        </Button>
        <Button
          size="small"
          onClick={() => {
            const res = cleanupStaleAudios({ destructive: false })
            setStatus(`limpieza suave: ${JSON.stringify(res)}`)
            poll()
          }}
        >
          limpieza suave
        </Button>
        <Button
          size="small"
          sx={{ color: "#ffa657" }}
          onClick={() => {
            const res = cleanupStaleAudios({ destructive: true })
            setStatus(`limpieza destructiva: ${JSON.stringify(res)}`)
            poll()
          }}
        >
          destructiva
        </Button>
      </Stack>
    </Box>
  )
}
